import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Layers, Split } from "lucide-react";
import { useState } from "react";

import { errorMessage } from "@/lib/api";
import type { SetProductPreview } from "./api";

/**
 * セット品の切り替え（畳み込み ⇔ 解除）の確認ダイアログ。**案件と見積で共通**。
 *
 * スイッチを押すと**明細の行数が変わる**（2 点のセットを 3 セットなら 2 行 ⇔
 * 6 行）。流し見の最中に押して行が増減すると戻し方が分からないので、押す前に
 * 何が起きるかを出す。
 *
 * **切り替えは案件と見積の両方を同じ形に作り直す 1 つの操作**なので、どちらから
 * 押しても内訳は同じ（API の下見も同じ応答を返す）。ここが共通なのはそのため。
 * 押した側と反対側の番号は「一緒に作り直すもの」として並べる。
 *
 * 内訳は API の下見から取る。**実行と同じ計画**を通すので、見せた内容と結果が
 * 食い違わない（受注の後付けの内訳と同じ作り）。切り替えられない場合はその理由
 * （`blockers`）をここに出し、実行のボタンを押せなくする。
 */
export default function SetProductDialog({
  owner,
  number,
  to,
  previewKey,
  fetchPreview,
  onClose,
  onConfirm,
}: {
  /** どちらの画面から押したか。文言と「反対側」の出し方が変わる。 */
  owner: "project" | "quotation";
  /** 押した側の番号（工番 or 見積番号）。 */
  number: string;
  /** 切り替え先。true でセット品にする（畳む）、false で解除する（分ける）。 */
  to: boolean;
  previewKey: unknown[];
  fetchPreview: () => Promise<SetProductPreview>;
  onClose: () => void;
  /** 実行する。失敗したらそのまま投げ返すと、理由をここに出す。 */
  onConfirm: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useQuery({ queryKey: previewKey, queryFn: fetchPreview });
  const plan = preview.data;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(errorMessage(e, "切り替えに失敗しました。"));
    } finally {
      setBusy(false);
    }
  };

  const ownerNoun = owner === "project" ? "案件" : "見積";
  // 一緒に作り直す反対側。案件から押したら見積、見積から押したら案件。
  const counterparts =
    plan == null
      ? []
      : owner === "project"
        ? plan.quotation_numbers.map((n) => `見積 ${n}`)
        : [`案件 ${plan.project_number}`];

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()} size="2xl">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          {to ? <Layers size={20} /> : <Split size={20} />}
          {to ? "セット品にする" : "セット品を解除する"}
        </ModalHeader>
        <ModalBody>
          {preview.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner size="sm" />
            </div>
          ) : plan == null ? (
            <p className="text-sm text-danger">
              {errorMessage(preview.error, "内訳を取得できませんでした。")}
            </p>
          ) : (
            <>
              <p className="text-sm">
                <span className="font-bold">{number}</span> を
                {to ? "セット品にします。" : "セット品から外します。"}
              </p>

              {plan.rebuilds_items ? (
                <>
                  <p className="text-sm">
                    明細は{" "}
                    <span className="font-bold">{plan.current_item_count} 行</span> から{" "}
                    <span className="font-bold">{plan.next_item_count} 行</span>{" "}
                    になります。
                  </p>
                  <ul className="list-disc space-y-0.5 rounded-lg bg-default-50 px-6 py-3 text-sm text-default-600">
                    {to ? (
                      <li>
                        {
                          "同じ部品（図番・ユニットNo・部品表No）の行を 1 行にまとめ、数量を客先購番ぶん合算します。"
                        }
                      </li>
                    ) : (
                      <li>
                        {
                          "客先購番ごとの行に分け直します。数量は発注書に書かれていた数（1 セットあたり）に戻ります。"
                        }
                      </li>
                    )}
                    {counterparts.length > 0 && (
                      <li>
                        {`${counterparts.join("・")} の明細も同じ形に作り直します。`}
                        {to
                          ? "工程別見積は残る行のものだけになります。"
                          : "工程別見積は分けた全行に写すので、見積の合計金額は変わりません。"}
                      </li>
                    )}
                    {plan.representative_po_number != null && (
                      <li>
                        {`作業実績と図面は、客先購番 ${plan.representative_po_number} の行に残ります。`}
                        {
                          "明細は作り直さずこの行を使い回すので、図面に押した QR もそのまま使えます。"
                        }
                      </li>
                    )}
                    <li>
                      {to
                        ? "明細ごとに入れた納期・出荷予定日・摘要は、残る行の値になります。"
                        : "明細ごとに入れた納期・出荷予定日・摘要は、分けた全行に写します。"}
                    </li>
                    <li>
                      {plan.set_count == null
                        ? "セット数は空になります。"
                        : `セット数は客先購番の本数から ${plan.set_count} セットになります。`}
                    </li>
                  </ul>
                </>
              ) : (
                <p className="text-sm text-default-600">
                  {`この${ownerNoun}には客先購番の行がないため、明細は変わりません（セット品の印だけが変わります）。`}
                </p>
              )}

              {plan.blockers.length > 0 && (
                <div className="space-y-1 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">
                  <p className="flex items-center gap-1.5 font-bold">
                    <AlertTriangle size={16} />
                    {`この${ownerNoun}は切り替えられません`}
                  </p>
                  <ul className="list-disc space-y-0.5 pl-5">
                    {plan.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </div>
              )}

              {error && <p className="text-sm text-danger">{error}</p>}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="confirm"
            isDisabled={plan == null || !plan.can_apply}
            isLoading={busy}
            onPress={() => void confirm()}
          >
            {to ? "セット品にする" : "解除する"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
